import type { AnyExtension } from "@tiptap/core";
import CharacterCount from "@tiptap/extension-character-count";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { common, createLowlight } from "lowlight";

/**
 * The editor's extension set (TASK-401, SPEC §14).
 *
 * Everything the SPEC lists as an editor feature is either in StarterKit
 * (paragraph, heading, bold, italic, strike, code, blockquote, lists,
 * horizontal rule, history) or added explicitly below.
 *
 * The editor knows nothing about GitHub, uploads or storage (AGENTS.md §13).
 * An image arrives in the document as a plain URL; where that URL came from is
 * somebody else's problem.
 */

export interface EditorExtensionsOptions {
  /** Shown when the document is empty. */
  readonly placeholder?: string;
  /** Soft cap, surfaced through `CharacterCount`. */
  readonly characterLimit?: number;
  /**
   * Called when the editor wants an image. Returns the public URL to insert,
   * or `null` to insert nothing.
   *
   * This is the *only* thing the editor knows about images, and it is injected
   * rather than imported so the editor package never grows an upload client.
   */
  readonly uploadImage?: (file: File) => Promise<string | null>;
}

/**
 * Build the extension list for one editor instance.
 *
 * `lowlight` is created per call rather than shared: `common` is a fixed
 * grammar set, and a fresh instance keeps editor instances from sharing mutable
 * highlighter state.
 */
export function createEditorExtensions(
  options: EditorExtensionsOptions = {},
): AnyExtension[] {
  const lowlight = createLowlight(common);

  return [
    StarterKit.configure({
      // Replaced by the lowlight-backed version below; leaving both registered
      // makes one of them silently win.
      codeBlock: false,
    }),

    CodeBlockLowlight.configure({ lowlight }),

    // Not in StarterKit, and not representable by a `*`/`_` Markdown marker —
    // the serializer emits `<u>` for it, which the site's sanitizer allows.
    Underline,

    Link.configure({
      openOnClick: false,
      autolink: true,
      // The renderer allows the same set, so a link the editor accepts is one
      // the site can actually render.
      protocols: ["http", "https", "mailto"],
      HTMLAttributes: { rel: "noopener noreferrer nofollow" },
    }),

    Image.configure({
      // Media is served from the site's own CDN host, never inlined.
      inline: false,
      allowBase64: false,
    }),

    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,

    TaskList,
    TaskItem.configure({ nested: true }),

    Placeholder.configure({
      placeholder: options.placeholder ?? "Write something…",
    }),

    CharacterCount.configure({
      ...(options.characterLimit === undefined ? {} : { limit: options.characterLimit }),
    }),
  ];
}
